import React from 'react';
import {
  alpha,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Logout,
  Person,
  Security,
  Devices,
  Apps,
  Language,
  Notifications,
  Accessibility,
  Visibility,
  Badge,
  Videocam,
  Keyboard,
  SettingsSuggest,
  Menu,
  Close,
  PostAdd
} from '@mui/icons-material';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  handleLogout: () => void;
  colors: any;
  isDark: boolean;
  goldColor: string;
  mobileSidebarOpen?: boolean;
  setMobileSidebarOpen?: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  handleLogout,
  colors,
  isDark,
  goldColor,
  mobileSidebarOpen = false,
  setMobileSidebarOpen = () => {},
}) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const SidebarItem = ({ id, label, icon: Icon, isHeader = false }: any) => {
    if (isHeader) return (
      <Typography variant="caption" fontWeight={800} color={colors.textSecondary} sx={{ 
        px: 2, 
        py: 1, 
        display: 'block', 
        mt: 2, 
        mb: 0.5, 
        textTransform: 'uppercase', 
        fontSize: '0.7rem', 
        letterSpacing: '0.05em' 
      }}>
        {label}
      </Typography>
    );

    const handleItemClick = () => {
      setActiveTab(id);
      if (isMobile && setMobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
    };

    return (
      <ListItem disablePadding sx={{ mb: 0.5, px: 1 }}>
        <ListItemButton 
          selected={activeTab === id}
          onClick={handleItemClick}
          sx={{ 
            borderRadius: '8px', 
            py: 0.8,
            px: 1.5,
            '&.Mui-selected': { 
              bgcolor: isDark ? alpha(colors.textSecondary, 0.15) : '#E0E0E0', 
              color: colors.textPrimary, 
              '&:hover': { bgcolor: isDark ? alpha(colors.textSecondary, 0.2) : '#D0D0D0' } 
            },
            '&:hover': { bgcolor: isDark ? alpha(colors.textSecondary, 0.08) : '#F0F0F0' }
          }}
        >
          {Icon && (
            <ListItemIcon sx={{ 
              minWidth: 32, 
              color: activeTab === id ? (isDark ? colors.textPrimary : goldColor) : colors.textSecondary 
            }}> 
              <Icon fontSize="small" /> 
            </ListItemIcon>
          )}
          <ListItemText 
            primary={label} 
            primaryTypographyProps={{ 
              fontSize: '0.95rem', 
              fontWeight: activeTab === id ? 600 : 500, 
              color: activeTab === id ? (isDark ? colors.textPrimary : goldColor) : colors.textSecondary 
            }} 
          />
        </ListItemButton>
      </ListItem>
    );
  };

  // Desktop Sidebar
  const desktopSidebar = (
    <Box sx={{ 
      width: 260, 
      bgcolor: colors.sidebarBg, 
      backdropFilter: 'blur(20px)',
      borderRight: `1px solid ${colors.borderColor}`,
      display: 'flex', 
      flexDirection: 'column',
      flexShrink: 0,
      height: 'calc(100vh - 64px)',
      position: 'sticky',
      top: 64,
    }}>
      <Box sx={{ 
        overflowY: 'auto', 
        flex: 1, 
        py: 2, 
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}>
        <SidebarItem isHeader label="User Settings" />
        <SidebarItem id="my_account" label="My Account" icon={Person} />
        <SidebarItem id="profiles" label="Communities" icon={Badge} />
        <SidebarItem id="posts" label="My Posts" icon={PostAdd} />
        <SidebarItem id="privacy" label="Data & Privacy" icon={Security} />
        <SidebarItem id="family" label="Family Center" icon={Visibility} />
        <SidebarItem id="authorized" label="Authorized Apps" icon={Apps} />
        <SidebarItem id="devices" label="Devices" icon={Devices} />
        <SidebarItem id="connections" label="Connections" icon={Language} />
        
        <Divider sx={{ my: 1, mx: 2, borderColor: colors.borderColor }} />
        
        <SidebarItem isHeader label="App Settings" />
        <SidebarItem id="appearance" label="Appearance" icon={SettingsSuggest} />
        <SidebarItem id="accessibility" label="Accessibility" icon={Accessibility} />
        <SidebarItem id="voice" label="Voice & Video" icon={Videocam} />
        <SidebarItem id="notifications" label="Notifications" icon={Notifications} />
        <SidebarItem id="keybinds" label="Keybinds" icon={Keyboard} />
      </Box>

      {/* Sidebar Logout */}
      <Box sx={{ p: 2, borderTop: `1px solid ${colors.borderColor}`, bgcolor: alpha(colors.headerBg, 0.5) }}>
        <Button 
          fullWidth 
          color="error" 
          startIcon={<Logout />} 
          onClick={handleLogout}
          sx={{ 
            justifyContent: 'flex-start', 
            textTransform: 'none', 
            fontWeight: 700, 
            borderRadius: '8px', 
            '&:hover': { bgcolor: alpha(colors.danger, 0.1) } 
          }}
        >
          Log Out
        </Button>
      </Box>
    </Box>
  );

  // Mobile Drawer
  const mobileDrawer = (
    <Drawer
      anchor="left"
      open={mobileSidebarOpen}
      onClose={() => setMobileSidebarOpen(false)}
      PaperProps={{
        sx: {
          width: 280,
          bgcolor: colors.sidebarBg,
          backdropFilter: 'blur(20px)',
          borderRight: `1px solid ${colors.borderColor}`,
        }
      }}
    >
      {/* Mobile Drawer Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.borderColor}`
      }}>
        <Typography variant="h6" sx={{ 
          fontWeight: 800, 
          color: colors.textPrimary,
          fontFamily: '"Inter", "Segoe UI", sans-serif'
        }}>
          Settings
        </Typography>
        <IconButton 
          onClick={() => setMobileSidebarOpen(false)}
          sx={{ color: colors.textSecondary }}
        >
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ 
        overflowY: 'auto', 
        flex: 1, 
        py: 2,
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}>
        <SidebarItem isHeader label="User Settings" />
        <SidebarItem id="my_account" label="My Account" icon={Person} />
        <SidebarItem id="profiles" label="Communities" icon={Badge} />
        <SidebarItem id="posts" label="My Posts" icon={PostAdd} />
        <SidebarItem id="privacy" label="Data & Privacy" icon={Security} />
        <SidebarItem id="family" label="Family Center" icon={Visibility} />
        <SidebarItem id="authorized" label="Authorized Apps" icon={Apps} />
        <SidebarItem id="devices" label="Devices" icon={Devices} />
        <SidebarItem id="connections" label="Connections" icon={Language} />
        
        <Divider sx={{ my: 1, mx: 2, borderColor: colors.borderColor }} />
        
        <SidebarItem isHeader label="App Settings" />
        <SidebarItem id="appearance" label="Appearance" icon={SettingsSuggest} />
        <SidebarItem id="accessibility" label="Accessibility" icon={Accessibility} />
        <SidebarItem id="voice" label="Voice & Video" icon={Videocam} />
        <SidebarItem id="notifications" label="Notifications" icon={Notifications} />
        <SidebarItem id="keybinds" label="Keybinds" icon={Keyboard} />
      </Box>

      {/* Mobile Drawer Logout */}
      <Box sx={{ p: 2, borderTop: `1px solid ${colors.borderColor}`, bgcolor: alpha(colors.headerBg, 0.5) }}>
        <Button 
          fullWidth 
          color="error" 
          startIcon={<Logout />} 
          onClick={handleLogout}
          sx={{ 
            justifyContent: 'flex-start', 
            textTransform: 'none', 
            fontWeight: 700, 
            borderRadius: '8px', 
            '&:hover': { bgcolor: alpha(colors.danger, 0.1) } 
          }}
        >
          Log Out
        </Button>
      </Box>
    </Drawer>
  );

  return (
    <>
      {!isMobile && desktopSidebar}
      {mobileDrawer}
    </>
  );
};

export default Sidebar;